import { useRouter } from "next/router";

import { GA_EVENT_IDS } from "@domains/common/constants/googleAnalyticsIds";
import {
  getColorButtonAnimation,
  iconButtonAnimation,
} from "@domains/common/utils/animationHelper";
import { getOpacityColor, grayScale, pink } from "@enkor/design/colors";
import { BubbleChatIcon, Share2Icon } from "@enkor/design/icons";
import { useAuthSession } from "@domains/auth/hooks/useAuthSession";
import { dataTrackEvent } from "@logics/utils/dataTrackEvent";
import { DATA_TRACKING_EVENT_NAME } from "@domains/common/constants/dataTrackEventName";
import WebViewBridge from "@logics/utils/webViewBridge";
import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";
import EventViewModel from "@domains/event/viewModel/EventViewModel";
import { EventInfoTrackingDataType } from "@domains/event/types/event";
import { useFetchMyReviewQuery } from "@domains/event/network/eventQueries";

import BaseButtonGroup from "../BaseButtonGroup/BaseButtonGroup";

import * as S from "./JoinedClosedButtonGroup.style";

interface JoinedClosedButtonGroupProps {
  eventId: string;
  eventInfo: EventInfoTrackingDataType;
  hostInfo: EventViewModel["host"];
  onClickChatRoom: () => void;
  onClickShare: () => void;
}

const JoinedClosedButtonGroup = ({
  eventId,
  eventInfo,
  hostInfo,
  onClickChatRoom,
  onClickShare,
}: JoinedClosedButtonGroupProps) => {
  const { push } = useRouter();

  const { isUserLoggedIn, guardRoute } = useAuthSession();

  const { data: myReviewData, isLoading: isMyReviewLoading } =
    useFetchMyReviewQuery(eventId, {
      enabled: !!eventId && isUserLoggedIn,
    });

  const handleClickReview = (eventId: string, reviewId?: number) => {
    if (!eventId || !hostInfo.id) return;

    guardRoute(() => {
      if (reviewId) {
        if (WebViewBridge.isInAppWebView()) {
          WebViewBridge.getInstance().navigateToProfileReview(
            String(hostInfo.id),
            String(reviewId),
          );
        } else {
          push(
            `${NAVIGATION_ROUTE.PROFILE.HREF}/${hostInfo.id}/review#${reviewId}`,
          );
        }

        return;
      }

      dataTrackEvent({
        eventName: DATA_TRACKING_EVENT_NAME.REVIEW.CLICK_START_REVIEW,
        properties: eventInfo,
      });

      if (WebViewBridge.isInAppWebView()) {
        WebViewBridge.getInstance().navigateToMyPageWriteReview(eventId);
      } else {
        push(`${NAVIGATION_ROUTE.MY_PAGE_REVIEW.HREF}/${eventId}`);
      }
    });
  };

  if (isMyReviewLoading) {
    return (
      <BaseButtonGroup
        actionButtonProps={{
          disabled: true,
        }}
        buttonContent="Closed"
        onClickShare={onClickShare}
      />
    );
  }

  return (
    <>
      {!myReviewData?.id && (
        <S.Phrase name="s">
          Host is waiting for your thoughts to be shared. 💬
        </S.Phrase>
      )}
      <S.ButtonGroupWrapper>
        <S.ShareButtonBox
          type="button"
          id={GA_EVENT_IDS.EVENT.GUEST_SHARE}
          {...iconButtonAnimation}
          onClick={onClickShare}
        >
          <Share2Icon strokeColor={grayScale.white} />
        </S.ShareButtonBox>
        <S.MessageButton
          type="button"
          {...iconButtonAnimation}
          onClick={onClickChatRoom}
        >
          <BubbleChatIcon size={24} strokeColor={grayScale.white} />
        </S.MessageButton>
        <S.FullButton
          $bgColor={myReviewData?.id ? "transparent" : pink.main}
          $borderColor={myReviewData?.id ? grayScale.white : "transparent"}
          {...getColorButtonAnimation({
            hoverColor: myReviewData?.id
              ? getOpacityColor(grayScale.white, 0.15)
              : pink[300],
            tapColor: myReviewData?.id
              ? getOpacityColor(grayScale.white, 0.3)
              : pink[200],
          })}
          onClick={() => handleClickReview(eventId, myReviewData?.id)}
        >
          {myReviewData?.id ? "See your thoughts" : "Share your thoughts"}
        </S.FullButton>
      </S.ButtonGroupWrapper>
    </>
  );
};

export default JoinedClosedButtonGroup;
